/**
 * AudioStorageService
 * 
 * Handles downloading and storing audio files for offline use.
 * Similar to the deck storage service, but specifically for audio files.
 */
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import { defaultSounds } from '@/components/game/soundlist';

// Type definitions for audio storage management
interface AudioManifest {
  version: number;
  lastUpdated: string;
  categories: AudioCategoryInfo[];
}

interface AudioCategoryInfo {
  name: string;
  isDownloaded: boolean;
  audioFiles: AudioFileInfo[];
}

interface AudioFileInfo {
  key: string;
  url: string;
  isDownloaded: boolean;
  localPath?: string;
}

export class AudioStorageService {
  private static MANIFEST_KEY = 'audio_manifest';
  private static AUDIO_DIRECTORY = 'audio';
  
  private manifest: AudioManifest | null = null;
  private isInitialized = false;
  private downloadQueue: { key: string, url: string, onProgress?: (progress: number) => void }[] = [];
  private isDownloading = false;
  private concurrentDownloads = 3;

  constructor() {
    this.initialize();
  }

  /**
   * Initialize the audio storage service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized || !this.isNativePlatform()) return;

    try {
      // Create base directory if it doesn't exist
      await this.ensureDirectory(AudioStorageService.AUDIO_DIRECTORY);
      
      // Load existing manifest or create a new one
      await this.loadManifest();
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize AudioStorageService:', error);
    }
  }

  /**
   * Load the audio manifest from preferences
   */
  private async loadManifest(): Promise<void> {
    try {
      const { value } = await Preferences.get({ key: AudioStorageService.MANIFEST_KEY });
      
      if (value) {
        this.manifest = JSON.parse(value);
      } else {
        // Initialize empty manifest
        this.manifest = {
          version: 1,
          lastUpdated: new Date().toISOString(),
          categories: this.generateDefaultCategories()
        };
        await this.saveManifest();
      }
    } catch (error) {
      console.error('Failed to load audio manifest:', error);
      // Initialize with empty manifest on error
      this.manifest = {
        version: 1,
        lastUpdated: new Date().toISOString(),
        categories: this.generateDefaultCategories()
      };
      await this.saveManifest();
    }
  }

  /**
   * Generate default audio categories based on the defaultSounds available
   */
  private generateDefaultCategories(): AudioCategoryInfo[] {
    const categories = new Map<string, AudioFileInfo[]>();
    
    // Process all default sounds to categorize them
    Object.entries(defaultSounds).forEach(([key, url]) => {
      const categoryName = key.split('.')[0]; // Extract category name from key
      
      if (!categories.has(categoryName)) {
        categories.set(categoryName, []);
      }
      
      categories.get(categoryName)?.push({
        key,
        url,
        isDownloaded: false
      });
    });
    
    // Convert Map to array of categories
    return Array.from(categories.entries()).map(([name, audioFiles]) => ({
      name,
      isDownloaded: false,
      audioFiles
    }));
  }

  /**
   * Save the current manifest to preferences
   */
  private async saveManifest(): Promise<void> {
    if (!this.manifest) return;
    
    try {
      await Preferences.set({
        key: AudioStorageService.MANIFEST_KEY,
        value: JSON.stringify(this.manifest)
      });
    } catch (error) {
      console.error('Failed to save audio manifest:', error);
    }
  }
  
  /**
   * Ensure a directory exists, creating it if necessary
   */
  private async ensureDirectory(path: string): Promise<void> {
    try {
      await Filesystem.mkdir({
        path,
        directory: Directory.Data,
        recursive: true
      });
    } catch (error: any) {
      // Ignore error if directory already exists
      if (error.message && !error.message.includes('exists')) {
        throw error;
      }
    }
  }

  /**
   * Check if a sound file is already downloaded
   */
  async isSoundDownloaded(key: string): Promise<boolean> {
    if (!this.isNativePlatform() || !this.manifest) return false;
    
    // Ensure the manifest is loaded
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    // Look for the sound file in all categories
    for (const category of this.manifest.categories) {
      const audioFile = category.audioFiles.find(file => file.key === key);
      if (audioFile && audioFile.isDownloaded) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Get the local path for a downloaded sound file
   */
  async getLocalSoundUrl(key: string): Promise<string | null> {
    if (!this.isNativePlatform() || !this.manifest) return null;
    
    // Ensure the manifest is loaded
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    // Look for the sound file in all categories
    for (const category of this.manifest.categories) {
      const audioFile = category.audioFiles.find(file => file.key === key);
      if (audioFile && audioFile.isDownloaded && audioFile.localPath) {
        try {
          // Return the local file URL
          const result = await Filesystem.getUri({
            path: audioFile.localPath,
            directory: Directory.Data
          });
          
          return result.uri;
        } catch (error) {
          console.warn('Error getting local sound URL:', error);
          return null;
        }
      }
    }
    
    return null;
  }

  /**
   * Download a single audio file if not already downloaded
   * @returns A promise that resolves to a boolean indicating if download was queued
   */
  async queueSoundDownloadIfNeeded(key: string, onProgress?: (progress: number) => void): Promise<boolean> {
    if (!this.isNativePlatform() || !this.manifest) return false;
    
    // Check if the sound is already downloaded
    const isAlreadyDownloaded = await this.isSoundDownloaded(key);
    if (isAlreadyDownloaded) {
      // If already downloaded, call the progress callback with 100%
      if (onProgress) {
        onProgress(100);
      }
      return false; // No download was queued
    }
    
    // Find the sound in the manifest
    let url: string | undefined;
    for (const category of this.manifest.categories) {
      const audioFile = category.audioFiles.find(file => file.key === key);
      if (audioFile) {
        url = audioFile.url;
        break;
      }
    }
    
    if (!url) {
      // If not found in manifest but exists in defaultSounds, use that
      url = defaultSounds[key];
      if (!url) {
        console.warn(`No URL found for sound key: ${key}`);
        return false;
      }
    }
    
    // Queue the sound for download
    this.queueSoundDownload(key, url, onProgress);
    return true; // Download was queued
  }

  /**
   * Queue a sound for download
   */
  queueSoundDownload(key: string, url: string, onProgress?: (progress: number) => void): void {
    if (!this.isNativePlatform()) return;
    
    this.downloadQueue.push({ key, url, onProgress });
    
    // Start processing queue if not already downloading
    if (!this.isDownloading) {
      this.processDownloadQueue();
    }
  }
  
  /**
   * Download all sounds for a specific category
   */
  async downloadCategory(categoryName: string, onProgress?: (progress: number) => void): Promise<boolean> {
    if (!this.isNativePlatform() || !this.manifest) return false;
    
    // Find the category
    const category = this.manifest.categories.find(c => c.name === categoryName);
    if (!category) {
      console.warn(`Category not found: ${categoryName}`);
      return false;
    }
    
    // Check if already downloaded
    if (category.isDownloaded) {
      if (onProgress) {
        onProgress(100);
      }
      return false;
    }
    
    // Queue all audio files in the category
    let queuedCount = 0;
    const totalFiles = category.audioFiles.length;
    
    for (const audioFile of category.audioFiles) {
      if (!audioFile.isDownloaded) {
        this.queueSoundDownload(audioFile.key, audioFile.url, (fileProgress) => {
          // Calculate overall category progress
          if (onProgress) {
            const overallProgress = Math.floor(((queuedCount / totalFiles) + (fileProgress / 100 / totalFiles)) * 100);
            onProgress(Math.min(overallProgress, 100));
          }
        });
        queuedCount++;
      }
    }
    
    // If no files were queued, everything was already downloaded
    if (queuedCount === 0) {
      if (onProgress) {
        onProgress(100);
      }
      return false;
    }
    
    return true;
  }
  
  /**
   * Process the download queue
   */
  private async processDownloadQueue(): Promise<void> {
    if (this.isDownloading || this.downloadQueue.length === 0 || !this.manifest) return;
    
    this.isDownloading = true;
    
    try {
      while (this.downloadQueue.length > 0) {
        // Take up to concurrentDownloads items from queue
        const batch = this.downloadQueue.splice(0, this.concurrentDownloads);
        
        // Process batch concurrently
        await Promise.all(batch.map(async (item) => {
          await this.downloadSound(item.key, item.url, item.onProgress);
        }));
      }
    } catch (error) {
      console.error('Error processing download queue:', error);
    } finally {
      this.isDownloading = false;
    }
  }
  
  /**
   * Download a single audio file
   */
  private async downloadSound(key: string, url: string, onProgress?: (progress: number) => void): Promise<void> {
    if (!this.isNativePlatform() || !this.manifest) return;
    
    try {
      // Create directory structure for audio files
      const categoryName = key.split('.')[0];
      const soundPath = `${AudioStorageService.AUDIO_DIRECTORY}/${categoryName}`;
      await this.ensureDirectory(soundPath);
      
      // Format safe filename from key
      const safeFilename = key.replace(/\./g, '_') + '.mp3';
      const localPath = `${soundPath}/${safeFilename}`;
      
      try {
        // Report start of download
        if (onProgress) {
          onProgress(10);
        }
        
        // Download file
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Report download progress
        if (onProgress) {
          onProgress(50);
        }
        
        const blob = await response.blob();
        
        // Convert blob to base64
        const base64Data = await this.blobToBase64(blob);
        
        // Write to filesystem
        await Filesystem.writeFile({
          path: localPath,
          data: base64Data,
          directory: Directory.Data
        });
        
        // Update manifest to mark sound as downloaded
        let found = false;
        for (const category of this.manifest.categories) {
          if (category.name === categoryName) {
            const audioFileIndex = category.audioFiles.findIndex(file => file.key === key);
            if (audioFileIndex >= 0) {
              category.audioFiles[audioFileIndex].isDownloaded = true;
              category.audioFiles[audioFileIndex].localPath = localPath;
              found = true;
              
              // Check if all files in category are downloaded
              const allDownloaded = category.audioFiles.every(file => file.isDownloaded);
              if (allDownloaded) {
                category.isDownloaded = true;
              }
            }
          }
        }
        
        // If the sound wasn't found in the manifest, add it
        if (!found) {
          // Find or create the category
          let category = this.manifest.categories.find(c => c.name === categoryName);
          if (!category) {
            category = {
              name: categoryName,
              isDownloaded: false,
              audioFiles: []
            };
            this.manifest.categories.push(category);
          }
          
          // Add the audio file
          category.audioFiles.push({
            key,
            url,
            isDownloaded: true,
            localPath
          });
        }
        
        // Save the updated manifest
        await this.saveManifest();
        
        // Report completion
        if (onProgress) {
          onProgress(100);
        }
        
        console.log(`Completed download of sound ${key}`);
      } catch (error) {
        console.error(`Failed to download sound ${key}:`, error);
        if (onProgress) {
          onProgress(0); // Report failure
        }
      }
    } catch (error) {
      console.error(`Failed to prepare download of sound ${key}:`, error);
    }
  }

  /**
   * Convert a Blob to base64 string
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        // Remove data URL prefix 
        const base64 = base64String.split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to convert blob to base64'));
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Check if we're running on a native platform
   */
  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }
  
  /**
   * Delete a downloaded sound
   */
  async deleteSound(key: string): Promise<void> {
    if (!this.isNativePlatform() || !this.manifest) return;
    
    // Find the sound in manifest
    for (const category of this.manifest.categories) {
      const audioFileIndex = category.audioFiles.findIndex(file => file.key === key);
      if (audioFileIndex >= 0 && category.audioFiles[audioFileIndex].isDownloaded) {
        const audioFile = category.audioFiles[audioFileIndex];
        
        try {
          if (audioFile.localPath) {
            await Filesystem.deleteFile({
              path: audioFile.localPath,
              directory: Directory.Data
            });
          }
          
          // Update manifest
          category.audioFiles[audioFileIndex].isDownloaded = false;
          delete category.audioFiles[audioFileIndex].localPath;
          
          // Update category download status
          category.isDownloaded = category.audioFiles.every(file => file.isDownloaded);
          
          // Save manifest
          await this.saveManifest();
        } catch (error) {
          console.error(`Failed to delete sound ${key}:`, error);
        }
        break;
      }
    }
  }
  
  /**
   * Delete all downloaded sounds for a category
   */
  async deleteCategory(categoryName: string): Promise<void> {
    if (!this.isNativePlatform() || !this.manifest) return;
    
    const category = this.manifest.categories.find(c => c.name === categoryName);
    if (!category) return;
    
    try {
      // Delete all files in the category
      const catPath = `${AudioStorageService.AUDIO_DIRECTORY}/${categoryName}`;
      await Filesystem.rmdir({
        path: catPath,
        directory: Directory.Data,
        recursive: true
      });
      
      // Update manifest
      for (const audioFile of category.audioFiles) {
        audioFile.isDownloaded = false;
        delete audioFile.localPath;
      }
      category.isDownloaded = false;
      
      // Save manifest
      await this.saveManifest();
    } catch (error) {
      console.error(`Failed to delete category ${categoryName}:`, error);
    }
  }
}

// Create singleton instance
export const audioStorageService = new AudioStorageService();